// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext } from 'react';
import authService from '../services/auth.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = () => {
            const currentUser = authService.getCurrentUser();
            const currentCompany = authService.getCompany();

            if (currentUser) {
                setUser(currentUser);
                setCompany(currentCompany);
            }
            setLoading(false);
        };

        initAuth();

        // Listen for localStorage changes (when user data is updated)
        const handleStorageChange = () => {
            const currentUser = authService.getCurrentUser();
            const currentCompany = authService.getCompany();
            if (currentUser) {
                setUser(currentUser);
                setCompany(currentCompany);
                //console.log('🔄 User data refreshed from localStorage');
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const login = async (email, password, companyDomain) => {
        const response = await authService.login(email, password, companyDomain);
        setUser(response.user);
        setCompany(response.company);
        return response;
    };

    const sendOTP = async (phone, companyDomain) => {
        return authService.sendOTP(phone, companyDomain);
    };

    const verifyOTP = async (phone, otp, companyDomain) => {
        const response = await authService.verifyOTP(phone, otp, companyDomain);
        if (response.user) {
            setUser(response.user);
            setCompany(response.company);
        }
        return response;
    };

    const resendOTP = async (phone, companyDomain) => {
        return authService.resendOTP(phone, companyDomain);
    };

    const logout = async () => {
        await authService.logout();
        setUser(null);
        setCompany(null);
    };

    const isAuthenticated = !!user;
    const isAdmin = user?.is_admin || user?.role_name === 'Super Admin' || user?.role_name === 'HR Admin' || user?.access_level === 'admin';
    const isManager = user?.is_manager || user?.role === 'manager' || user?.role_name === 'Manager' || user?.access_level === 'manager';

    const value = {
        user,
        company,
        loading,
        login,
        sendOTP,
        verifyOTP,
        resendOTP,
        logout,
        isAuthenticated,
        isAdmin,
        isManager,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};